import React, { useState } from 'react';
import { BsClipboard, BsClipboardCheck } from 'react-icons/bs';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Icon,
  IconButton,
  Input,
  Select,
  Text,
  Tooltip
} from '@chakra-ui/react';

import { useMailgunLogs } from '../../services/mailgun-service';
import colors from '../../styles/theme/foundation/colors';

const DAY_OPTIONS = [7, 14, 30];

const VERDICT_LABEL = {
  delivered: 'Delivered',
  failed: 'Failed',
  accepted_only: 'Accepted only'
};

const VERDICT_COLOR = {
  delivered: 'green.600',
  failed: 'red.600',
  accepted_only: 'orange.600'
};

/**
 * The API already strips internal Mailgun fields, but belt-and-braces: never
 * let them into the clipboard even if the payload shape changes upstream.
 */
const SENSITIVE_KEYS = new Set([
  'storage',
  'apiKeyId',
  'api-key-id',
  'originatingIp',
  'originating-ip',
  'userVariables',
  'user-variables',
  'flags'
]);

const toSafeJson = value => JSON.stringify(
  value,
  (key, val) => (SENSITIVE_KEYS.has(key) ? undefined : val),
  2
);

const isValidEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const formatTimestamp = timestamp => {
  const date = new Date(timestamp);

  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
};

const eventDetail = event => [
  event.severity,
  event.reason,
  event.deliveryStatus?.code,
  event.deliveryStatus?.message
].filter(part => part !== null && part !== undefined && part !== '').join(' | ');

/**
 * The most diagnostic event for a message: a failure if there is one,
 * otherwise the delivery confirmation, otherwise the latest event.
 */
const messageDetail = message => {
  const events = message.events ?? [];
  const significant = events.find(event => ['failed', 'rejected'].includes(event.event))
    ?? events.find(event => event.event === 'delivered')
    ?? events[events.length - 1];

  return significant ? eventDetail(significant) : '';
};

const messageKey = message => message.messageId ?? message.timestamp;

const PinsAdminEmailLogsTable = () => {
  const [email, setEmail] = useState('');
  const [days, setDays] = useState(7);
  const [submittedParams, setSubmittedParams] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  const { data, isLoading, isFetching, isError, error } = useMailgunLogs(submittedParams ?? {}, { enabled: Boolean(submittedParams?.email) });

  const handleSearch = () => {
    if (!isValidEmail(email)) {
      return;
    }

    setSubmittedParams({ email: email.trim().toLowerCase(), days });
  };

  const handleCopy = async message => {
    await navigator.clipboard.writeText(toSafeJson(message));

    setCopiedKey(messageKey(message));
    setTimeout(() => setCopiedKey(null), 1500);
  };

  const suppressions = data?.suppressions ?? {};
  const suppressionEntries = Object.entries(suppressions);
  const messages = data?.messages ?? [];
  const showResults = Boolean(submittedParams) && !isLoading && !isFetching && !isError;

  return (
    <Box bg={colors.white} maxH="700px" overflowY="auto" p={6} w="1050px">
      <Text textStyle="displayXs">PINS Admin</Text>

      <Flex alignItems="center" justifyContent="space-between" mt={4}>
        <Text textStyle="bodyLg">Search Mailgun delivery logs by recipient email</Text>

        <Flex alignItems="center" gap={3}>
          <Input
            placeholder="Recipient email address"
            type="email"
            value={email}
            w="300px"
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
          />
          <Select value={days} w="140px" onChange={e => setDays(Number(e.target.value))}>
            {DAY_OPTIONS.map(option => (
              <option key={option} value={option}>
                {`Last ${option} days`}
              </option>
            ))}
          </Select>
          <Button isDisabled={!isValidEmail(email)} isLoading={isLoading || isFetching} onClick={handleSearch}>
            Search
          </Button>
        </Flex>
      </Flex>

      {!submittedParams && (
        <Text mt={4}>Enter an email address to look up its delivery history.</Text>
      )}

      {(isLoading || isFetching) && <Text mt={4}>Loading...</Text>}

      {isError && (
        <Alert borderRadius="md" mt={4} status="error">
          <AlertIcon />
          <AlertDescription>
            {error?.message || 'Something went wrong fetching the Mailgun logs. Please try again.'}
          </AlertDescription>
        </Alert>
      )}

      {showResults && suppressionEntries.length > 0 && (
        <Box mt={4}>
          {suppressionEntries.map(([list, entry]) => (
            <Alert borderRadius="md" key={list} mb={2} status="error">
              <AlertIcon />
              <AlertDescription>
                {`On the ${list} suppression list since ${entry.createdAt ?? 'an unknown date'}`}
                {entry.error ? ` — ${entry.error}` : ''}
                . Mailgun refuses every send to this address until the entry is deleted, which requires an engineer.
              </AlertDescription>
            </Alert>
          ))}
        </Box>
      )}

      {showResults && !messages.length && (
        <Text mt={4}>{`No Mailgun events found for ${submittedParams.email} in the last ${data?.days ?? days} days.`}</Text>
      )}

      {showResults && messages.length > 0 && (
        <Box p={4} textAlign="left">
          <Grid gap={6} gridTemplateColumns="repeat(12, 1fr);">
            <GridItem alignSelf="center" colSpan={2} justifySelf="left" ml={2} textStyle="bodyLg500">Timestamp</GridItem>
            <GridItem alignSelf="center" colSpan={3} justifySelf="left" ml={2} textStyle="bodyLg500">Subject</GridItem>
            <GridItem alignSelf="center" colSpan={2} justifySelf="left" ml={2} textStyle="bodyLg500">Insured</GridItem>
            <GridItem alignSelf="center" colSpan={2} justifySelf="left" ml={2} textStyle="bodyLg500">Verdict</GridItem>
            <GridItem alignSelf="center" colSpan={2} justifySelf="left" ml={2} textStyle="bodyLg500">Detail</GridItem>
            <GridItem alignSelf="center" colSpan={1} justifySelf="left" ml={2} textStyle="bodyLg500">Copy</GridItem>
          </Grid>

          {messages.map(message => (
            <Grid
              gap={6}
              gridTemplateColumns="repeat(12, 1fr);"
              key={messageKey(message)}
              my={4}
            >
              <GridItem alignSelf="center" colSpan={2} justifySelf="left" ml={2}>
                {formatTimestamp(message.timestamp)}
              </GridItem>
              <GridItem alignSelf="center" colSpan={3} justifySelf="left" ml={2}>
                {message.subject}
              </GridItem>
              <GridItem alignSelf="center" colSpan={2} justifySelf="left" ml={2}>
                {message.insured?.name ?? '—'}
              </GridItem>
              <GridItem alignSelf="center" colSpan={2} justifySelf="left" ml={2}>
                <Tooltip label={message.verdict?.message}>
                  <Text color={VERDICT_COLOR[message.verdict?.status] ?? 'greyScale.700'} textStyle="bodyLg500">
                    {VERDICT_LABEL[message.verdict?.status] ?? message.verdict?.status}
                  </Text>
                </Tooltip>
              </GridItem>
              <GridItem alignSelf="center" colSpan={2} justifySelf="left" ml={2} wordBreak="break-word">
                {messageDetail(message)}
              </GridItem>
              <GridItem alignSelf="center" colSpan={1} justifySelf="left" ml={2}>
                <Tooltip label={copiedKey === messageKey(message) ? 'Copied!' : 'Copy JSON'}>
                  <IconButton
                    aria-label="copy-message-json"
                    icon={<Icon as={copiedKey === messageKey(message) ? BsClipboardCheck : BsClipboard} boxSize="18px" />}
                    size="xs"
                    variant="textGrey"
                    onClick={() => handleCopy(message)}
                  />
                </Tooltip>
              </GridItem>
            </Grid>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default PinsAdminEmailLogsTable;
